import { useEffect, useMemo, useState } from 'react'
import { Link } from 'gatsby'
import { checkEnviromentIsB2B } from 'src/utils/checkEnviroment'
import { useProductLink } from 'src/sdk/product/useProductLinkV2'
import SKUSelectorBoughtTogether from 'src/components/product/SkuSelector/SKUSelectorBoughtTogether'
import type { ActiveItem } from 'src/components/ShelfLinx/ShelfBoughtTogether/types'
import { useMobile } from 'src/hooks/useMobile'
import CustomBox from 'src/components/common/CustomBox'
import {
  INSTALLMENTS_PRODUCT_CARD_EXPERIMENT_ID,
  SHELF_EXPERIMENT_ID,
} from 'src/components/common/StatsigExposure/StatsigExposure'
import { Statsig } from 'src/sdk/statsig'
import { isBestSellerBadgeEligible } from 'src/utils/restructure/isBestSellerBadgeEligible'

import type { Product as ProductRestructure } from '../types/product'
import type { ProductItem } from '../types/productItem'
import ProductCardHeaderV3 from './ProductCardHeaderV3'
import ProductCardPriceV3 from './ProductCardPriceV3'
import ProductCardInstallments from './ProductCardInstallments'
import ProductBrandV3 from './ProductBrandV3'
import ProductQuantitySKUV3 from './ProductQuantitySKUV3'
import ProductWellRatedFlag from '../ProductCard/ProductWellRatedFlag'
import FavoritesCounterCompact from '../ProductCard/FavoritesCounterCompact'
import ProductAuthorityBadge from '../ProductCard/ProductAuthorityBadge'
import FakeSalesCounter from '../ProductCard/FakeSalesCounter'
import { LastUnitForProductCardWrapper } from '../sections/Sidebar/LastUnitsFlag/LastUnitForProductCardWrapper'
import { ShippingCampaignFlag } from '../ProductCard/ShippingCampaignFlag'
import { useProductCardShippingCampaign } from '../ProductCard/hooks'

export interface AmplitudeData {
  term?: string | null
  sort?: string
  page?: number
  selectedFacets?: Array<{ key: string; value: string }>
  itemsPerPage?: number
  totalCountOfRegisters?: number
  base?: string
}

interface ProductCardV3Props {
  product: ProductRestructure & { decisionId?: string }
  drag?: boolean
  index: number
  shelfRef?: string
  shelfEngine?: string
  viewSelectVariation?: boolean
  handleChangedItemSKU?: (item: ActiveItem | undefined) => void
  isShelfBoughtTogether?: boolean
  amplitudeData?: AmplitudeData
  type?: string
  title?: string
  isSlider?: boolean
  productNameWrapper?: React.ElementType
  id?: string
  customFnOnAdd?: (e: React.MouseEvent<HTMLButtonElement>) => void
  eventSource?: string
  isPdpShelf?: boolean
}

export default function ProductCardV3({
  product,
  viewSelectVariation = false,
  index,
  shelfRef,
  shelfEngine,
  drag,
  handleChangedItemSKU,
  isShelfBoughtTogether = false,
  amplitudeData,
  type,
  title,
  isSlider,
  productNameWrapper = 'h3',
  id,
  customFnOnAdd,
  eventSource,
  isPdpShelf = false,
}: Readonly<ProductCardV3Props>) {
  const { isMobile } = useMobile()

  const [skuSelected, setSkuSelected] = useState<ProductItem>(product.items[0])
  const [imagesList, setImagesList] = useState(product.items[0].images)
  const [SkuId, setSkuId] = useState<string | undefined>()
  const isExperimentalVitrine =
    Statsig.useStatsigFeatureGateValue(SHELF_EXPERIMENT_ID)

  const isInstallmentsProductCardEligible = Statsig.useStatsigFeatureGateValue(
    INSTALLMENTS_PRODUCT_CARD_EXPERIMENT_ID
  )

  const {
    linkText,
    productName,
    flagCashbackHighlight,
    items: [
      {
        sellerDefault: {
          sellerOffer: { cashback },
        },
      },
    ],
  } = product

  const isB2B = checkEnviromentIsB2B()

  const colors = new Set(product?.items?.map((item) => item.Cor))
  const colorsArray = Array.from(colors).flat()
  const uniqueColorsCount = new Set(colorsArray).size

  const showSkuSelector = !isShelfBoughtTogether && uniqueColorsCount > 1

  const isBestSellerEligible = useMemo(() => {
    return isBestSellerBadgeEligible({ productId: product.productId })
  }, [product.productId])

  const shippingCampaign = useProductCardShippingCampaign({
    price: skuSelected?.sellerDefault?.sellerOffer?.price,
    cashPrice: skuSelected?.sellerDefault?.sellerOffer?.cashPrice,
    sellerName: skuSelected?.sellerDefault?.sellerName,
    categoryTree: product?.categories,
  })

  useEffect(() => {
    const newSku = SkuId
      ? product.items.find((item) => item.itemId === SkuId)
      : product.items[0]

    if (!newSku) {
      return
    }

    setSkuSelected(newSku)
    setImagesList(newSku.images)
  }, [SkuId, product])

  const handleChangedSku = (sku: ActiveItem) => {
    const newSku = product.items.find((item) => item.itemId === sku.itemId)

    if (!newSku) {
      return
    }

    setSkuSelected(newSku)
    setImagesList(newSku.images)
    setSkuId(sku.itemId)

    if (handleChangedItemSKU) {
      const newActiveItem: ActiveItem = {
        itemId: newSku.itemId,
        listedPrice: newSku.offer.listPrice,
        price: newSku.offer.price,
        name: newSku.name,
        quantity: newSku.sellerDefault.sellerOffer.quantity,
        sellerId: newSku.sellerDefault.sellerId,
      }

      handleChangedItemSKU(newActiveItem)
    }
  }

  const productLink = useProductLink({
    product,
    currentSku: skuSelected,
    slug: `${linkText}`,
    index,
    shelfRef,
    shelfEngine,
    amplitudeData,
    type,
    title,
    id,
    shippingBadgePromotion: shippingCampaign.analyticsBadgeKey,
  })

  return (
    <div
      data-testid="product-card"
      className={`product-card-v2 group w-full min-w-[158px] sm:min-w-[254px] ${
        isPdpShelf
          ? 'md:min-w-[400px]'
          : isExperimentalVitrine
          ? 'md:min-w-[267px]'
          : 'md:min-w-[302px]'
      } flex justify-start flex-col min-h-[350px] md:min-h-[500px]`}
    >
      <div className="relative">
        <ProductCardHeaderV3
          product={product}
          currentSku={skuSelected}
          isBestSellerEligible={isBestSellerEligible}
          link={productLink}
          Images={imagesList}
          drag={drag}
          title={title}
          isShelfBoughtTogether={isShelfBoughtTogether}
          isSlider={isSlider}
          hasFirstImagePriority={index < 4}
          customFnOnAdd={customFnOnAdd}
          eventSource={eventSource}
        />

        {showSkuSelector && (
          <div
            className={`absolute left-2 bottom-9 z-5 w-full px-3 md:px-0 transition-opacity duration-200 ${
              isMobile
                ? 'opacity-0'
                : 'opacity-0 pointer-events-none md:group-hover:opacity-100 md:group-hover:pointer-events-auto'
            } [&>*]:!mb-0`}
          >
            <SKUSelectorBoughtTogether
              productItems={product}
              currentSku={skuSelected}
              handleChangedItemSKU={handleChangedSku}
              viewSelectVariation={viewSelectVariation}
            />
          </div>
        )}
      </div>

      <Link
        {...productLink}
        className="w-full px-3 md:px-0 flex flex-col gap-1"
      >
        <ProductAuthorityBadge
          product={product}
          textClassName="text-[9px] md:text-[10px]"
        />

        <LastUnitForProductCardWrapper
          skuSelected={skuSelected}
          fallbackProductId={product.productId}
        />

        <ProductWellRatedFlag selectedFacets={amplitudeData?.selectedFacets} />

        <div className="mt-1 flex min-h-[16px] items-center justify-between gap-2">
          <ProductBrandV3 brand={product.brand} />

          {skuSelected.isAvailable && (
            <FavoritesCounterCompact
              productId={product.productId}
              dataDyId="favorites-counter-card"
            />
          )}
        </div>

        <CustomBox
          as={productNameWrapper}
          className="font-inter font-regular text-restructure-primary text-sm leading-5 line-clamp-2 md:line-clamp-1"
        >
          {productName}
        </CustomBox>

        <FakeSalesCounter productId={product.productId} />

        <ProductCardPriceV3 product={product} currentSku={skuSelected} />

        {skuSelected.isAvailable && isInstallmentsProductCardEligible && (
          <ProductCardInstallments currentSku={skuSelected} />
        )}

        {skuSelected.isAvailable && cashback && !isB2B && (
          <>
            {flagCashbackHighlight ? (
              <div className="font-inter max-w-fit text-green6 text-[10px] md:text-[12px] font-medium leading-4 px-[10px] py-[2px] bg-restructure-background-cashback mt-1">
                10% - cashback em dobro!
              </div>
            ) : (
              <div
                className={`font-inter max-w-fit text-green6 ${
                  isExperimentalVitrine
                    ? 'md:text-[11px] leading-[16px]'
                    : 'md:text-[12px]'
                } text-[10px] font-medium leading-4 px-[10px] py-[2px] bg-restructure-background-cashback mt-1`}
              >
                {cashback}
              </div>
            )}
          </>
        )}

        {!skuSelected.isAvailable && (
          <p className="font-inter text-restructure-primary text-[14px] leading-12">
            Produto indisponível
          </p>
        )}

        {isMobile && product?.skuSpecifications[0]?.values.length > 1 && (
          <ProductQuantitySKUV3 SkuSpecification={product.skuSpecifications} />
        )}
      </Link>

      {shippingCampaign.isEligible && (
        <div className="w-full px-3 md:px-0">
          <ShippingCampaignFlag
            isEligible={shippingCampaign.isEligible}
            ctaLabel={shippingCampaign.badgeText ?? shippingCampaign.ctaLabel}
            ctaActionText={shippingCampaign.ctaActionText}
            badgeSubtext={shippingCampaign.badgeSubtext}
            modal={shippingCampaign.modal}
          />
        </div>
      )}
    </div>
  )
}
